import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ReactFsLightbox from 'fslightbox-react';
import { VideoThumbnailGenerator } from 'browser-video-thumbnail-generator';
import { apiHandler } from '../../../api/apihandler';
import { handleImageUpload } from '../../../api/uploads';
import {
  CHALLENGE_DETAILS,
  CHALLENGE_PERTICIPATE,
  CHALLENGE_PERTICIPATE_MEDIA_POST,
  FEED_LIKE,
  FEED_LIKES,
  FEED_DELETE,
  REPORT_FLAGS_LIST,
  POST_FLAG,
  TRANSLATION_TRANSLATE,
} from '../../../api/constants';
import CommentList from '../../../components/comment/commentList';
import CommentBox from '../../../components/comment/commentBox';
import ParticipateList from '../../../components/challenge/participateList';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import { Avatar, formatTimeLeft } from './home-app-page';
import { formatRelativeTime, apiSucceeded, apiErrorMessage } from '../../../views/dashboard/feed/feedUtils';
import {
  DEFAULT_TRANSLATION_LANGUAGE,
  fetchTranslationLanguages,
  normalizeTranslationLanguages,
} from '../../../utilities/translationLanguages.js';
import '../../../views/dashboard/social/social-list-page.css';
import './shape-challenges-page.css';
import '../../../components/feeds/feedPostCard.css';
import './home-app-page.css';
import './challenge-detail-page.css';

const FsLightbox = ReactFsLightbox.default ? ReactFsLightbox.default : ReactFsLightbox;
const TRANSLATE_LANG_STORAGE_KEY = 'challengePostTranslateLanguage';

// Fresh nemonto-themed rebuild of the challenge participate flow (photo/
// video + note), modeled on home-app-page.js's MadeItEntryScreen but
// supporting multiple attachments like the original participate modal did.
// Wired to the same CHALLENGE_PERTICIPATE(+MEDIA_POST) endpoints/sequence.
const ChallengeParticipateScreen = ({ post, onClose }) => {
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]); // [{file, preview}]
  const [videos, setVideos] = useState([]); // [{file, preview, thumbFile, thumbPreview}]
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => () => {
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    videos.forEach((vid) => URL.revokeObjectURL(vid.preview));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAddImages(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setImages((prev) => [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  }

  async function handleAddVideos(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      let thumbFile = null;
      let thumbPreview = null;
      try {
        const generator = new VideoThumbnailGenerator(preview);
        const thumbnail = await generator.getThumbnail();
        thumbPreview = thumbnail?.thumbnail;
        if (thumbPreview) {
          const blob = await (await fetch(thumbPreview)).blob();
          thumbFile = new File([blob], `${+new Date()}.jpg`, { type: 'image/jpeg' });
        }
      } catch (err) {
        console.error(err);
      }
      setVideos((prev) => [...prev, { file, preview, thumbFile, thumbPreview }]);
    }
  }

  function removeImage(idx) {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  function removeVideo(idx) {
    setVideos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  async function submit() {
    if (!content.trim() && images.length === 0 && videos.length === 0) {
      Swal.fire('Required', 'Please write something or add a photo/video.', 'warning');
      return;
    }

    const hasMedia = images.length > 0 || videos.length > 0;
    setIsLoading(true);
    setUploadStatus('Submitting…');
    try {
      const res = await apiHandler(CHALLENGE_PERTICIPATE, 'POST', {
        comment: content,
        is_completed: hasMedia ? 0 : 1,
        post_id: post?.id,
        type: '',
      });
      if (!apiSucceeded(res)) {
        throw new Error(apiErrorMessage(res, 'Failed to submit your entry.'));
      }

      if (hasMedia) {
        const participateId = res?.data?.data?.participate?.id;
        for (const img of images) {
          setUploadStatus('Uploading photo…');
          const url = await handleImageUpload(img.file, 'participateImages/', 'image/jpeg');
          await apiHandler(CHALLENGE_PERTICIPATE_MEDIA_POST, 'POST', {
            user_post_participate_id: participateId,
            post_id: post?.id,
            path: url,
            media_type: '0',
            thumb: '',
          });
        }
        for (const vid of videos) {
          setUploadStatus('Uploading video…');
          const url = await handleImageUpload(vid.file, 'participateVideos/', 'video/*');
          const thumbUrl = vid.thumbFile ? await handleImageUpload(vid.thumbFile, 'participateVideoThumb/', 'image/jpeg') : '';
          await apiHandler(CHALLENGE_PERTICIPATE_MEDIA_POST, 'POST', {
            user_post_participate_id: participateId,
            post_id: post?.id,
            path: url,
            media_type: '1',
            thumb: thumbUrl,
          });
        }

        setUploadStatus('Finishing up…');
        const finalRes = await apiHandler(CHALLENGE_PERTICIPATE, 'POST', {
          content,
          type: '',
          is_completed: '1',
          post_id: post?.id,
        });
        if (!apiSucceeded(finalRes)) {
          throw new Error(apiErrorMessage(finalRes, 'Failed to finish submitting your entry.'));
        }
      }

      Swal.fire('Success', 'Your entry has been submitted!', 'success').then(() => onClose(true));
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setIsLoading(false);
      setUploadStatus('');
    }
  }

  return (
    <div>
      <div className="nmha-entry-header">
        <button type="button" onClick={() => onClose(false)} disabled={isLoading} style={{ fontSize: 20 }}>✕</button>
        <h2 className="nmha-entry-title">Participate</h2>
        <div style={{ width: 24 }} />
      </div>

      <div className="nmha-entry-wrap">
        {post ? (
          <div className="nmha-entry-ref-card">
            <div className="nmha-entry-ref-img-wrap">
              {post?.user_post_images?.[0]?.image_path ? <img src={post.user_post_images[0].image_path} alt="" /> : <MediaPlaceholderIcon size={32} />}
            </div>
            <div>
              <p className="nmha-entry-ref-label">Entering challenge</p>
              <p className="nmha-entry-ref-title">{post?.content?.slice(0, 60) || 'Challenge'}</p>
            </div>
          </div>
        ) : null}

        <p className="nmha-entry-label">Your photos/videos</p>
        <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddImages} />
        <input ref={videoInputRef} type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={handleAddVideos} />
        <div className="nmch-participate-media-grid">
          {images.map((img, i) => (
            <div key={`img-${i}`} className="nmch-participate-media-tile">
              <img src={img.preview} alt="" />
              <button type="button" className="nmch-participate-media-remove" onClick={() => removeImage(i)} disabled={isLoading}>✕</button>
            </div>
          ))}
          {videos.map((vid, i) => (
            <div key={`vid-${i}`} className="nmch-participate-media-tile">
              {vid.thumbPreview ? <img src={vid.thumbPreview} alt="" /> : <video src={vid.preview} muted />}
              <button type="button" className="nmch-participate-media-remove" onClick={() => removeVideo(i)} disabled={isLoading}>✕</button>
            </div>
          ))}
          <div className="nmch-participate-add-tile" onClick={() => !isLoading && imageInputRef.current?.click()}>
            <span className="material-symbols-outlined">photo_camera</span>
            Photo
          </div>
          <div className="nmch-participate-add-tile" onClick={() => !isLoading && videoInputRef.current?.click()}>
            <span className="material-symbols-outlined">videocam</span>
            Video
          </div>
        </div>

        <p className="nmha-entry-label">Your note</p>
        <textarea
          className="nmha-entry-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Tell us about your entry…"
          disabled={isLoading}
        />

        <div className="nmha-entry-footer">
          <button type="button" className="nmha-entry-post-btn" onClick={submit} disabled={isLoading}>Submit entry</button>
        </div>
      </div>

      {isLoading && (
        <div className="nmha-overlay">
          <div className="nmha-overlay-card">
            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{uploadStatus || 'Submitting…'}</p>
            <p style={{ fontSize: 12, color: '#9a8c84', margin: 0 }}>Don&apos;t close this screen</p>
          </div>
        </div>
      )}
    </div>
  );
};

const ChallengeDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  // Which bucket (live/upcoming/past) the challenges grid listed this card
  // under, if we arrived from there - undefined on a direct link/refresh,
  // in which case status falls back to expire_at/winner alone.
  const bucketHint = location.state?.bucket;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState('view');

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likesUserList, setLikesUserList] = useState([]);
  const [showLikesModal, setShowLikesModal] = useState(false);

  const [commentCount, setCommentCount] = useState(0);
  const [refreshCommentList, setRefreshCommentList] = useState(0);
  const [participatesCount, setParticipatesCount] = useState(0);
  const [isParticipate, setIsParticipate] = useState(0);
  const [winnerParticipateId, setWinnerParticipateId] = useState(0);

  const [showFlagModal, setShowFlagModal] = useState(false);
  const [reportFlags, setReportFlags] = useState([]);

  const [imageController, setImageController] = useState({ toggler: false, slide: 1 });

  const [translationLanguages, setTranslationLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_TRANSLATION_LANGUAGE.code);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [translatedContent, setTranslatedContent] = useState('');
  const [showOriginalContent, setShowOriginalContent] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContentCache, setTranslatedContentCache] = useState({});
  const [lastTranslatedLanguage, setLastTranslatedLanguage] = useState('');

  async function loadDetail() {
    setLoading(true);
    try {
      const res = await apiHandler(CHALLENGE_DETAILS, 'POST', { post_id: id });
      if (apiSucceeded(res)) {
        const d = res?.data?.data?.userPost;
        setDetail(d);
        setLiked(d?.is_like === 1);
        setLikeCount(d?.like_count || 0);
        setCommentCount(d?.comment_count || 0);
        setParticipatesCount(d?.participates_count || 0);
        setIsParticipate(d?.is_participate || 0);
        setWinnerParticipateId(d?.winner_participate_id ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { languages } = await fetchTranslationLanguages();
        if (isMounted && Array.isArray(languages) && languages.length > 0) {
          setTranslationLanguages(languages);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const availableTranslationLanguages = translationLanguages.length > 0
    ? normalizeTranslationLanguages(translationLanguages)
    : [DEFAULT_TRANSLATION_LANGUAGE];

  useEffect(() => {
    const preferred = localStorage.getItem(TRANSLATE_LANG_STORAGE_KEY);
    const codes = availableTranslationLanguages.map((l) => l?.code).filter(Boolean);
    if (preferred && codes.includes(preferred)) {
      setSelectedLanguage(preferred);
    } else if (codes.length > 0) {
      setSelectedLanguage(codes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationLanguages]);

  async function toggleLike() {
    const next = !liked;
    const newCount = next ? likeCount + 1 : likeCount - 1;
    setLiked(next);
    setLikeCount(newCount);
    try {
      await apiHandler(FEED_LIKE, 'POST', { post_id: id });
    } catch (e) {
      setLiked(!next);
      setLikeCount(likeCount);
    }
  }

  async function openLikesList() {
    setShowLikesModal(true);
    try {
      const res = await apiHandler(FEED_LIKES, 'POST', { post_id: id });
      if (apiSucceeded(res)) setLikesUserList(res?.data?.data?.userLikes || []);
    } catch (e) {
      console.error(e);
    }
  }

  function confirmDelete() {
    Swal.fire({
      title: 'Delete this challenge?',
      text: 'This will remove the challenge post permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await apiHandler(FEED_DELETE, 'DELETE', { post_id: id });
        if (apiSucceeded(res)) {
          Swal.fire('Deleted', 'Your challenge has been deleted.', 'success').then(() => navigate(-1));
        } else {
          Swal.fire('Error', apiErrorMessage(res, 'Unable to delete this challenge.'), 'error');
        }
      } catch (e) {
        console.error(e);
      }
    });
  }

  async function openReportFlags() {
    try {
      const res = await apiHandler(REPORT_FLAGS_LIST, 'POST');
      if (apiSucceeded(res)) {
        setReportFlags(res?.data?.data?.reportFlags || []);
        setShowFlagModal(true);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function flagPost(flagId) {
    try {
      const res = await apiHandler(POST_FLAG, 'POST', { post_id: id, report_flag_id: flagId });
      setShowFlagModal(false);
      if (apiSucceeded(res)) {
        Swal.fire('Reported', "Thanks — we'll review this challenge.", 'success');
      } else {
        Swal.fire('Error', apiErrorMessage(res, 'Unable to submit report.'), 'error');
      }
    } catch (e) {
      console.error(e);
    }
  }

  function onLanguageChange(code) {
    setSelectedLanguage(code);
    localStorage.setItem(TRANSLATE_LANG_STORAGE_KEY, code);
    if (translatedContentCache?.[code]) {
      setTranslatedContent(translatedContentCache[code]);
      setLastTranslatedLanguage(code);
      setShowOriginalContent(false);
    } else {
      setTranslatedContent('');
      setShowOriginalContent(false);
    }
  }

  async function translatePostText() {
    if (!detail?.content || !selectedLanguage) return;
    if (translatedContentCache?.[selectedLanguage]) {
      setTranslatedContent(translatedContentCache[selectedLanguage]);
      setLastTranslatedLanguage(selectedLanguage);
      setShowOriginalContent(false);
      return;
    }
    setIsTranslating(true);
    try {
      const res = await apiHandler(TRANSLATION_TRANSLATE, 'POST', { post_id: id, target_lang: selectedLanguage });
      const translatedText =
        res?.data?.data?.translated ||
        res?.data?.data?.translated_text ||
        res?.data?.data?.translatedText ||
        res?.data?.data?.translation ||
        res?.data?.data?.text ||
        res?.data?.data?.content ||
        '';
      if (apiSucceeded(res) && translatedText) {
        setTranslatedContent(translatedText);
        setLastTranslatedLanguage(selectedLanguage);
        setShowOriginalContent(false);
        setTranslatedContentCache((prev) => ({ ...prev, [selectedLanguage]: translatedText }));
      } else {
        Swal.fire('Translation Failed', apiErrorMessage(res, 'Unable to translate this post right now.'), 'error');
      }
    } catch (e) {
      Swal.fire('Translation Failed', e?.message || 'Unable to translate this post right now.', 'error');
    } finally {
      setIsTranslating(false);
    }
  }

  function handleParticipateClose(didSubmit) {
    setFlow('view');
    if (didSubmit) {
      setIsParticipate(1);
      setParticipatesCount((prev) => prev + 1);
    }
  }

  if (!localStorage.getItem('uzoneAuthToken')) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  const authorName = `${detail?.user?.first_name || ''} ${detail?.user?.last_name || ''}`.trim() || 'User';
  const activeContent = showOriginalContent || !translatedContent ? detail?.content : translatedContent;
  const cookTime = detail?.cook_time ?? 0;
  const servings = detail?.serving_for ?? 0;
  const calorieCount = detail?.calorie_count ?? 0;
  const cuisineName = detail?.cuisine_type?.cuisine?.name || '';
  const ingredients = detail?.recipe_ingredients || [];

  const isWinnerDeclared = winnerParticipateId > 0;
  const timeLeft = formatTimeLeft(detail?.expire_at);
  const expired = detail?.expire_at ? new Date(detail.expire_at).getTime() < Date.now() : false;
  // The API tags the post with its real challenge_status directly
  // ("live"/"upcoming"/"past") - this is authoritative and, unlike
  // expire_at, is never null for a started-or-finished challenge. Prefer
  // it over the navigation-state bucket hint (which only exists when we
  // arrived from the grid) and fall back to the expire_at-derived
  // `expired` flag only if neither is available (e.g. stale deep link).
  const statusHint = detail?.challenge_status || bucketHint;

  let statusClass;
  let statusLabel;
  if (statusHint === 'upcoming') {
    statusClass = 'upcoming';
    statusLabel = 'Upcoming';
  } else if (statusHint === 'past' || isWinnerDeclared) {
    statusClass = isWinnerDeclared ? 'winner' : 'ended';
    statusLabel = isWinnerDeclared ? 'Winner declared' : 'Challenge ended';
  } else if (statusHint !== 'live' && expired) {
    statusClass = 'ended';
    statusLabel = 'Challenge ended';
  } else {
    const isEnding = !!timeLeft && !timeLeft.startsWith('1d') && timeLeft.includes('h left');
    statusClass = isEnding ? 'ending' : 'live';
    statusLabel = timeLeft ? `${isEnding ? 'Ending' : 'Live'} · ${timeLeft}` : 'Live now';
  }

  const canParticipate = detail?.is_own !== 1 && !isWinnerDeclared
    && statusHint !== 'upcoming' && statusHint !== 'past'
    && (statusHint === 'live' || !expired);
  const isEnded = statusClass === 'ended';

  const images = detail?.user_post_images || [];
  const lightboxSources = images.map((img) => img.image_path);
  const lightboxTypes = images.map((img) => (img.media_type === 1 ? 'video' : 'image'));

  return (
    <div className="nmha-root">
      <div className="nmha-app">
        <main className="nmha-feed-col">
          {flow === 'participate' ? (
            <ChallengeParticipateScreen post={detail} onClose={handleParticipateClose} />
          ) : (
            <>
              <div className="nmha-detail-back-row">
                <button type="button" className="nmha-back-btn" onClick={() => navigate(-1)}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span> Back
                </button>
              </div>

              {loading ? (
                <p className="nmha-loading-inline">Loading…</p>
              ) : !detail ? (
                <p className="nmha-empty-text" style={{ padding: '0 32px' }}>Challenge not found.</p>
              ) : (
                <div className="nmha-detail-wrap">
                  <div className="nmha-detail-author-row">
                    <Avatar url={detail?.user?.user_setting?.photo} name={authorName} className="nmha-detail-author-avatar" />
                    <div>
                      <div className="nmha-detail-author-name">{authorName}</div>
                      <div className="nmha-detail-author-meta">Posted {formatRelativeTime(detail?.created_at)}</div>
                    </div>
                    <Dropdown style={{ marginLeft: 'auto' }}>
                      <Dropdown.Toggle as="span" bsPrefix="nmha-post-more" style={{ cursor: 'pointer' }}>⋯</Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        {detail?.is_own === 1 ? (
                          <Dropdown.Item onClick={confirmDelete}>Delete challenge</Dropdown.Item>
                        ) : (
                          <Dropdown.Item onClick={openReportFlags}>Report challenge</Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <div className={`nmha-detail-media-box nmch-detail-media-box${isEnded ? ' nmch-detail-media-ended' : ''}`}>
                    {images.length > 0 ? (
                      images[0].media_type === 1
                        ? <img src={images[0].thumb || images[0].image_path} alt="" onClick={() => setImageController({ toggler: !imageController.toggler, slide: 1 })} role="button" tabIndex={0} />
                        : <img src={images[0].image_path} alt="" onClick={() => setImageController({ toggler: !imageController.toggler, slide: 1 })} role="button" tabIndex={0} />
                    ) : (
                      <MediaPlaceholderIcon size={150} />
                    )}
                    <span className={`ch-status-pill ${statusClass}`}>
                      {statusClass === 'live' && <span className="ch-dot" />}
                      {statusLabel}
                    </span>
                  </div>

                  {images.length > 1 && (
                    <div className={`nmch-detail-gallery-thumbs${isEnded ? ' nmch-detail-gallery-ended' : ''}`}>
                      {images.map((img, i) => (
                        <div
                          key={img.id}
                          className={`nmch-detail-gallery-thumb${i === 0 ? ' active' : ''}`}
                          onClick={() => setImageController({ toggler: !imageController.toggler, slide: i + 1 })}
                          role="button"
                          tabIndex={0}
                        >
                          <img src={img.media_type === 1 ? (img.thumb || img.image_path) : img.image_path} alt="" />
                          {img.media_type === 1 && <span className="nmch-play-badge">▶</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {detail?.content ? (
                    <>
                      <p className="nmha-detail-desc">{activeContent}</p>
                      {lastTranslatedLanguage && translatedContent && !showOriginalContent && (
                        <p style={{ fontSize: 12, color: '#9a8c84', marginTop: -12, marginBottom: 12 }}>
                          Translated to {availableTranslationLanguages.find((l) => l?.code === lastTranslatedLanguage)?.label || lastTranslatedLanguage}
                        </p>
                      )}
                      <button type="button" className="nmch-translate-btn" onClick={() => setShowTranslateModal(true)}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>translate</span> Translate
                      </button>
                    </>
                  ) : null}

                  <div className="nmha-detail-action-bar">
                    <button type="button" className={`nmha-detail-action${liked ? ' liked' : ''}`} onClick={toggleLike}>
                      <span className={`material-symbols-outlined${liked ? ' filled' : ''}`} style={{ fontSize: 18 }}>favorite</span>
                      <span onClick={(e) => { e.stopPropagation(); openLikesList(); }}>{likeCount}</span>
                    </button>
                    <span
                      className="nmha-detail-action"
                      onClick={() => document.getElementById('nmch-detail-comments')?.scrollIntoView({ behavior: 'smooth' })}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chat_bubble_outline</span> {commentCount}
                    </span>
                    <span
                      className="nmha-detail-action"
                      onClick={() => document.getElementById('nmch-detail-participants')?.scrollIntoView({ behavior: 'smooth' })}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>emoji_events</span> {participatesCount} entries
                    </span>
                    {canParticipate && (
                      isParticipate === 1 ? (
                        <span className="nmha-detail-action nmch-participated-pill">Already participated</span>
                      ) : (
                        <button type="button" className="nmha-detail-action nmch-participate-btn" onClick={() => setFlow('participate')}>
                          Participate
                        </button>
                      )
                    )}
                  </div>

                  {(cookTime > 0 || servings > 0 || calorieCount > 0) && (
                    <div className="nmha-detail-stats">
                      {cookTime > 0 && <div className="nmha-detail-stat"><b>{cookTime} min</b><span>Cook time</span></div>}
                      {servings > 0 && <div className="nmha-detail-stat"><b>{servings}</b><span>Servings</span></div>}
                      {calorieCount > 0 && <div className="nmha-detail-stat"><b>{calorieCount}</b><span>Calories</span></div>}
                    </div>
                  )}

                  {cuisineName && (
                    <div className="nmha-detail-cuisine-tags">
                      <span className="nmha-detail-cuisine-tag">{cuisineName}</span>
                    </div>
                  )}

                  {ingredients.length > 0 && (
                    <section className="nmha-detail-section">
                      <p className="nmha-detail-section-head">Ingredients</p>
                      {ingredients.map((ing, i) => (
                        <div key={i} className="nmha-detail-row">
                          <span className="nmha-detail-bullet" />
                          <span>{ing.amount ? `${ing.amount} ${ing.item}` : ing.item}</span>
                        </div>
                      ))}
                    </section>
                  )}

                  <section className="nmha-detail-section" id="nmch-detail-comments">
                    <p className="nmha-detail-section-head">Comments ({commentCount})</p>
                    <CommentList
                      postId={id}
                      refreshCommentList={refreshCommentList}
                      setRefreshCommentList={setRefreshCommentList}
                      onCommentAdded={() => setCommentCount((c) => c + 1)}
                      onCommentDeleted={() => setCommentCount((c) => Math.max(0, c - 1))}
                    />
                    <CommentBox
                      postId={id}
                      commentId=""
                      setRefreshCommentList={setRefreshCommentList}
                      onCommentAdded={() => setCommentCount((c) => c + 1)}
                    />
                  </section>

                  <section className="nmha-detail-section" id="nmch-detail-participants">
                    <p className="nmha-detail-section-head">Participants ({participatesCount})</p>
                    <ParticipateList postId={id} winnerParticipateId={winnerParticipateId} setWinnerParticipateId={setWinnerParticipateId} />
                  </section>
                </div>
              )}
            </>
          )}
        </main>

        <aside className="nmha-right-rail">
          <PeopleYouMayKnowWidget />
          <MyStatsWidget />
        </aside>
      </div>

      <Modal show={showLikesModal} onHide={() => setShowLikesModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Likes</Modal.Title></Modal.Header>
        <Modal.Body>
          {likesUserList.length === 0 ? (
            <p className="text-muted mb-0">No likes yet.</p>
          ) : likesUserList.map((elm) => (
            <div key={elm?.id} style={{ padding: '6px 0' }}>{elm?.user?.first_name} {elm?.user?.last_name}</div>
          ))}
        </Modal.Body>
      </Modal>

      <Modal show={showFlagModal} onHide={() => setShowFlagModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Report reason</Modal.Title></Modal.Header>
        <Modal.Body>
          {reportFlags.map((f) => (
            <div key={f?.id} style={{ padding: '10px 4px', cursor: 'pointer', borderBottom: '1px solid #eee' }} onClick={() => flagPost(f?.id)}>
              {f?.name}
            </div>
          ))}
        </Modal.Body>
      </Modal>

      <Modal
        show={showTranslateModal}
        onHide={() => setShowTranslateModal(false)}
        fullscreen="sm-down"
        className="translate-post-modal"
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom" style={{ padding: '12px 20px', minHeight: '50px' }}>
          <Modal.Title className="fw-bold">Translate Post</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <label className="form-label fw-semibold mb-1">Original Text</label>
            <div
              className="form-control rounded"
              style={{ minHeight: '80px', maxHeight: '120px', whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', overflowY: 'auto', padding: '12px', lineHeight: '1.6', border: '1px solid #dee2e6' }}
            >
              {detail?.content || '-'}
            </div>
          </div>

          <div>
            <label className="form-label fw-semibold mb-1">Select Target Language</label>
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select form-select-sm flex-grow-1"
                value={selectedLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
                style={{ minHeight: '38px', fontSize: '14px' }}
              >
                {availableTranslationLanguages.map((lang) => (
                  <option key={lang?.code} value={lang?.code}>{lang?.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={translatePostText}
                disabled={isTranslating}
                style={{ minHeight: '38px', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {isTranslating ? 'Translating…' : (translatedContent ? 'Translate Again' : 'Translate')}
              </button>
            </div>
          </div>

          {translatedContent ? (
            <div>
              <label className="form-label fw-semibold mb-1">Translated Result</label>
              <div
                className="form-control rounded"
                style={{ minHeight: '80px', maxHeight: '120px', whiteSpace: 'pre-wrap', backgroundColor: '#eef6ff', overflowY: 'auto', padding: '12px', lineHeight: '1.6', border: '1px solid #bfe7ff' }}
              >
                {translatedContent}
              </div>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      {lightboxSources.length > 0 && (
        <FsLightbox
          toggler={imageController.toggler}
          sources={lightboxSources}
          slide={imageController.slide}
          types={lightboxTypes}
        />
      )}
    </div>
  );
};

export default ChallengeDetailPage;
